// Convert country code to full name
export const getCountryFullName = (country) => {
  if (!country) return '';
  const countryMap = {
    'US': 'United States',
    'USA': 'United States',
    'United States': 'United States',
    'CA': 'Canada',
    'Canada': 'Canada',
    'MX': 'Mexico',
    'Mexico': 'Mexico',
    'UK': 'United Kingdom',
    'GB': 'United Kingdom',
    'United Kingdom': 'United Kingdom'
  };
  return countryMap[country] || country;
};

// Helper function to format address from separate fields
export const formatAddressFromFields = (street, city, state, zip, country) => {
  const addressParts = [];
  
  if (street) addressParts.push(street);
  
  const cityStateZip = [];
  if (city) cityStateZip.push(city);
  if (state) cityStateZip.push(state);
  if (zip) cityStateZip.push(zip);
  
  if (cityStateZip.length > 0) {
    addressParts.push(cityStateZip.join(', '));
  }
  
  if (country) addressParts.push(getCountryFullName(country));
  
  return addressParts.join('\n');
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Format company address
export const formatCompanyAddress = (company) => {
  if (!company) return '';
  
  let address = '';
  
  if (company.address) address += company.address;
  
  if (company.city || company.state || company.zip_code) {
    address += address ? '\n' : '';
    if (company.city) address += company.city;
    if (company.state) {
      if (company.city) address += ', ';
      address += company.state;
    }
    if (company.zip_code) {
      if (company.city || company.state) address += ' ';
      address += company.zip_code;
    }
  }
  
  if (company.country) {
    address += address ? '\n' : '';
    address += getCountryFullName(company.country);
  }
  
  return address;
};

// Safe parse float
export const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  const numValue = parseFloat(value);
  return isNaN(numValue) ? 0 : numValue;
};

// Get today's date in YYYY-MM-DD format
export const getTodayString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Calculate PO totals
export const calculatePOTotals = (lineItems, discount, taxRate, shippingFee, handlingFee, otherFees, creditApplied, discountType = 'percent') => {
  const subtotal = lineItems.reduce((sum, item) => {
    const qty = safeParseFloat(item.quantity);
    const price = safeParseFloat(item.unit_price);
    return sum + (qty * price);
  }, 0);

  const discountValue = safeParseFloat(discount);
  const discountAmount = discountType === 'flat'
    ? Math.min(discountValue, subtotal)
    : (subtotal * discountValue) / 100;
  
  const effectiveSubtotal = Math.max(0, subtotal - discountAmount);
  const taxRateValue = safeParseFloat(taxRate);
  const taxAmount = effectiveSubtotal * (taxRateValue / 100);
  
  const shipping = safeParseFloat(shippingFee);
  const handling = safeParseFloat(handlingFee);
  const other = safeParseFloat(otherFees);
  const credit = safeParseFloat(creditApplied);
  
  const totalBeforeCredit = effectiveSubtotal + taxAmount + shipping + handling + other;
  const totalAmount = Math.max(0, totalBeforeCredit - credit);
  
  return {
    subtotal: Number(subtotal.toFixed(2)),
    discount_amount: Number(discountAmount.toFixed(2)),
    tax_amount: Number(taxAmount.toFixed(2)),
    total_before_credit: Number(totalBeforeCredit.toFixed(2)),
    total_amount: Number(totalAmount.toFixed(2)),
    amount_due: Number(totalAmount.toFixed(2))
  };
};